<!-- src/views/TransferDashboard.vue -->
<template>
  <div class="min-h-screen bg-gray-900 text-gray-100 font-sans p-4 md:p-8">
    
    <!-- Header Superior -->
    <header class="flex flex-col md:flex-row justify-between items-center mb-8 border-b border-gray-700 pb-4">
      <div>
        <h1 class="text-2xl font-bold text-white tracking-wide">TRANSFERENCIAS</h1>
        <p class="text-sm text-cyan-400">{{ fechaRangoDisplay }}</p>
      </div>
      <div class="mt-4 md:mt-0 flex items-center gap-4">
        <span class="text-orange-500 font-bold text-lg">
            {{ agenciaNombre }}
        </span>
      </div>
    </header>

    <!-- Mensaje de Carga -->
    <div v-if="isLoading" class="text-center py-20">
      <svg class="animate-spin h-12 w-12 text-cyan-400 mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
      <p class="text-xl text-gray-400">Cargando datos de transferencias...</p>
    </div>

    <!-- Mensaje si no hay datos -->
    <div v-else-if="!hasData" class="text-center py-20">
      <p class="text-xl text-gray-400">No se encontraron datos para el período seleccionado.</p>
      <button @click="$router.push('/transfers')" class="mt-4 text-cyan-400 hover:underline">Volver al inicio</button>
    </div>
    
    <!-- Mensaje de Error -->
    <div v-else-if="errorMessage" class="text-center py-20">
      <p class="text-xl text-red-400 mb-4">{{ errorMessage }}</p>
      <button @click="$router.push('/transfers')" class="px-6 py-3 bg-cyan-500 hover:bg-cyan-400 text-gray-900 font-bold rounded-lg transition-colors">
        Volver al inicio
      </button>
    </div>


    <!-- Contenido Principal (Solo si hay datos) -->
    <div v-else>
        
        <!-- Sección de Resúmenes (Tarjetas Superiores - ANCHO COMPLETO) -->
        <div class="space-y-6 mb-8">
            
            <!-- Tarjeta: Por Agencia -->
            <div class="bg-gray-800 rounded-xl p-6 border border-gray-700 shadow-lg relative overflow-hidden group">
                <div class="absolute top-0 left-0 w-1 h-full bg-cyan-500"></div>
                <h3 class="text-cyan-400 font-semibold mb-4 uppercase text-sm tracking-wider">Resumen Agencia</h3>
                <div class="overflow-x-auto">
                    <table class="w-full text-left text-sm">
                        <thead>
                            <tr class="text-gray-500 border-b border-gray-700">
                                <th class="pb-2 font-medium">AGENCIA</th>
                                <th class="pb-2 font-medium text-right">REGISTROS</th>
                                <th class="pb-2 font-medium text-right">PASAJEROS</th>
                                <th class="pb-2 font-medium text-right">MONTO</th>
                            </tr>
                        </thead>
                        <tbody class="divide-y divide-gray-700/50">
                            <tr v-for="(item, index) in dataResumen.agencia" :key="index" class="hover:bg-gray-700/30 transition-colors">
                                <td class="py-3 font-bold text-white">{{ item.agencia }}</td>
                                <td class="py-3 text-right text-gray-300">{{ item.registros }}</td>
                                <td class="py-3 text-right text-gray-300">{{ item.pasajeros }}</td>
                                <td class="py-3 text-right font-mono" :class="getColorClass(item.monto)">
                                    {{ formatCurrency(item.monto) }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <!-- Tarjeta: Por Servicio -->
            <div class="bg-gray-800 rounded-xl p-6 border border-gray-700 shadow-lg relative overflow-hidden">
                <div class="absolute top-0 left-0 w-1 h-full bg-lime-500"></div>
                <h3 class="text-lime-400 font-semibold mb-4 uppercase text-sm tracking-wider">Por Servicio</h3>
                <div class="overflow-x-auto">
                    <table class="w-full text-left text-sm">
                        <thead>
                            <tr class="text-gray-500 border-b border-gray-700">
                                <th class="pb-2 font-medium">SERVICIO</th>
                                <th class="pb-2 font-medium text-right">REGISTROS</th>
                                <th class="pb-2 font-medium text-right">PASAJEROS</th>
                                <th class="pb-2 font-medium text-right">MONTO</th>
                            </tr>
                        </thead>
                        <tbody class="divide-y divide-gray-700/50">
                            <tr v-for="(item, index) in dataResumen.servicio" :key="index" class="hover:bg-gray-700/30 transition-colors">
                                <td class="py-3 font-bold text-white">{{ item.servicio }}</td>
                                <td class="py-3 text-right text-gray-300">{{ item.registros }}</td>
                                <td class="py-3 text-right text-gray-300">{{ item.pasajeros }}</td>
                                <td class="py-3 text-right font-mono" :class="getColorClass(item.monto)">
                                    {{ formatCurrency(item.monto) }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <!-- Sección: Detalle de Reservas (Tabla Principal con Paginación) -->
        <div class="bg-gray-800 rounded-xl border border-gray-700 shadow-xl overflow-hidden">
            
            <div class="p-4 border-b border-gray-700 flex flex-col md:flex-row justify-between items-center bg-gray-800/50">
                <div class="flex items-center gap-2 mb-2 md:mb-0">
                    <svg class="w-5 h-5 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>
                    <h3 class="text-lg font-bold text-white">DETALLE DE RESERVAS</h3>
                </div>
                <div class="text-sm text-gray-400">
                    Mostrando <span class="text-white font-bold">{{ paginationInfo.start }}</span> - <span class="text-white font-bold">{{ paginationInfo.end }}</span> de <span class="text-white font-bold">{{ totalItems }}</span> registros
                </div>
            </div>

            <div class="overflow-x-auto">
                <table class="w-full text-left border-collapse">
                    <thead class="bg-gray-900/50 text-gray-400 text-xs uppercase tracking-wider">
                        <tr>
                            <th class="p-4 font-semibold">RL</th>
                            <th class="p-4 font-semibold">PASAJERO</th>
                            <th class="p-4 font-semibold">CANTIDAD</th>
                            <th class="p-4 font-semibold">SERVICIO</th>
                            <th class="p-4 font-semibold">FECHA SERVICIO</th>
                            <th class="p-4 font-semibold text-right">TOTAL</th>
                            <th class="p-4 font-semibold text-right">$/PAX</th>
                        </tr>
                    </thead>
                    <tbody class="divide-y divide-gray-700 text-sm">
                        <tr v-for="(row, idx) in paginatedData" :key="idx" class="hover:bg-gray-700/40 transition-colors group">
                            <td class="p-4 font-mono font-bold text-white">{{ row.reserva_id }}</td>
                            <td class="p-4 text-gray-300">{{ row.nombre }} {{ row.apellido }}</td>
                            <td class="p-4 text-gray-300 font-medium">{{ row.pax }}</td>
                            <td class="p-4 text-white font-semibold">{{ row.servicio }}</td>
                            <td class="p-4 text-gray-400 font-mono">{{ formatDate(row.fecha_servicio) }}</td>
                            <td class="p-4 text-right font-mono font-bold" :class="getColorTotalClass(row.origen_id, row.subcanal_id)">
                                {{ formatCurrency(row.total) }}
                            </td>
                            <td class="p-4 text-right font-mono text-gray-400">
                                {{ row.precio_pax > 0 ? formatCurrency(row.precio_pax) : '-' }}
                            </td>
                        </tr>
                        
                        <tr v-if="paginatedData.length === 0">
                            <td colspan="9" class="p-8 text-center text-gray-500">
                                No hay detalles en esta página.
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            
            <!-- Controles de Paginación -->
            <div class="p-4 border-t border-gray-700 flex flex-col md:flex-row justify-between items-center gap-4">
                <div class="flex items-center gap-2 text-sm text-gray-400">
                    <span>Mostrar:</span>
                    <select v-model="itemsPerPage" @change="currentPage = 1" class="bg-gray-700 border border-gray-600 text-white text-sm rounded-lg focus:ring-cyan-500 focus:border-cyan-500 block p-1">
                        <option value="10">10</option>
                        <option value="20">20</option>
                        <option value="50">50</option>
                        <option value="100">100</option>
                    </select>
                    <span>registros</span>
                </div>

                <div class="flex items-center gap-2">
                    <button @click="prevPage" :disabled="currentPage === 1" class="px-3 py-1 bg-gray-700 rounded hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed text-sm transition-colors">
                        ← Anterior
                    </button>
                    <span class="text-sm text-gray-300 mx-2">
                        Pág {{ currentPage }} de {{ totalPages }}
                    </span>
                    <button @click="nextPage" :disabled="currentPage === totalPages" class="px-3 py-1 bg-gray-700 rounded hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed text-sm transition-colors">
                        Siguiente →
                    </button>
                </div>
            </div>
        </div>
    </div>
  </div>
</template>

<script>
import apiService from '@/services/api'; 

export default {
  name: 'TransferDashboard',
  data() {
    return {
      // Datos que vienen del Backend
      receivedData: null,
      
      // Variables de Paginación
      currentPage: 1,
      itemsPerPage: 10,
      
      // Metadata UI
      agenciaNombre: '',
      fechaDesde: '',
      fechaHasta: '',
      
      // Estado de carga
      isLoading: true,
      errorMessage: ''
    };
  },
  computed: {
    hasData() {
        return this.receivedData && this.receivedData.data;
    },
    dataResumen() {
      if (!this.hasData) return { agencia: [], servicio: [] };
      return {
        agencia: this.receivedData.data.resumen_agencia || [],
        servicio: this.receivedData.data.resumen_servicio || []
      };
    },
    allDataDetalle() {
      if (!this.hasData) return [];
      return this.receivedData.data.detalle || [];
    },
    totalItems() {
      return this.allDataDetalle.length;
    },
    totalPages() {
      return Math.ceil(this.totalItems / this.itemsPerPage);
    },
    paginatedData() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + parseInt(this.itemsPerPage);
      return this.allDataDetalle.slice(start, end);
    },
    paginationInfo() {
      if (this.totalItems === 0) return { start: 0, end: 0 };
      const start = (this.currentPage - 1) * this.itemsPerPage + 1;
      let end = this.currentPage * this.itemsPerPage;
      if (end > this.totalItems) end = this.totalItems;
      return { start, end };
    },
    fechaRangoDisplay() {
       if (!this.fechaDesde || !this.fechaHasta) return '';
       // Formatear para mostrar bonito: "DD/MM/YYYY - DD/MM/YYYY"
       const desde = this.formatDateDisplay(this.fechaDesde);
       const hasta = this.formatDateDisplay(this.fechaHasta);
       return `${desde} al ${hasta}`;
    }
  },
  methods: {
    formatCurrency(value) {
      if (value === null || value === undefined) return '-';
      return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(value);
    },
    getColorClass(value) {
      if (value > 0) return 'text-lime-400';
      if (value < 0) return 'text-red-500';
      return 'text-gray-400';
    },
    getColorTotalClass(origen, sub) {
      return (origen == sub) ? 'text-lime-400' : 'text-red-500';
    },
    // Formato para la tabla (DD/MM/YYYY)
    formatDate(dateString) {
      if (!dateString) return '';
      // Asumiendo formato YYYY-MM-DD del backend
      const parts = dateString.split('-');
      if (parts.length === 3) {
        return `${parts[2]}/${parts[1]}/${parts[0]}`;
      }
      return dateString;
    },
    // Formato para el header display
    formatDateDisplay(dateString) {
        if (!dateString) return '';
        return this.formatDate(dateString);
    },
    nextPage() {
      if (this.currentPage < this.totalPages) this.currentPage++;
    },
    prevPage() {
      if (this.currentPage > 1) this.currentPage--;
    },
    async cargarDatos() {
      this.isLoading = true;
      this.errorMessage = '';
      
      // 1. Leer parámetros de la URL (Ahora son fechas)
      const { codigo, fecha_desde, fecha_hasta } = this.$route.query;
      
      // 2. Validar que existan los parámetros
      if (!codigo || !fecha_desde || !fecha_hasta) {
        this.errorMessage = 'Parámetros de consulta no válidos.';
        this.isLoading = false;
        setTimeout(() => {
          this.$router.push('/transfers');
        }, 2000);
        return;
      }
      
      // 3. Configurar metadata para mostrar
      this.fechaDesde = fecha_desde;
      this.fechaHasta = fecha_hasta;
      
      try {
        // 4. Llamar al servicio API con el nuevo método de rango
        // Asumiendo que creaste getLoginAgenciaWithRange o similar en apiService
        const response = await apiService.getDatosAgencia(codigo, fecha_desde, fecha_hasta);
    
        if (response.ok) {
          this.receivedData = response; 
          this.agenciaNombre = response.nombre_agencia || 'AGENCIA';
        } else {
          throw new Error(response.message || 'Error al obtener datos');
        }

      } catch (error) {
        console.error('Error al cargar datos:', error);
        this.errorMessage = error.message || 'Error de conexión con el servidor.';
      } finally {
        this.isLoading = false;
      }
    }
  },
  created() {
    // Cargar datos al crear el componente
    this.cargarDatos();
  },
  watch: {
    // Si cambian los parámetros de la URL
    '$route.query': {
      handler() {
        this.cargarDatos();
      },
      immediate: false
    }
  }
};
</script>

<style scoped>
/* Estilos scrollbar igual que antes */
::-webkit-scrollbar { width: 8px; height: 8px; }
::-webkit-scrollbar-track { background: #1f2937; }
::-webkit-scrollbar-thumb { background: #4b5563; border-radius: 4px; }
::-webkit-scrollbar-thumb:hover { background: #6b7280; }
</style>